import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, MapPin, Search, Instagram, Star, ExternalLink, X, Package, Calendar, Clock, Users } from 'lucide-react';
import { getAdminLocations, createLocation, updateLocation, deleteLocation } from '../services/adminApi';
import FileUpload from '../components/FileUpload';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LocationsManager = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('basic'); // basic, social, faqs, packages, slots
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    price: 0,
    description: '',
    image: '',
    displayOrder: 0,
    coordinates: { lat: 0, lng: 0 },
    contactNumber: '',
    email: '',
    available: true,
    // Booking Settings
    bookingEnabled: true,
    pricePerJumper: 0,
    maxJumpersPerSlot: 10,
    slotDuration: 60,
    partialPaymentEnabled: false,
    partialPaymentAmount: 0,
    // Social Media
    instagramLink: '',
    gmbLink: '',
    gmbRating: 4.9,
    gmbReviewCount: 0,
    // Stats Section
    overValue: '44,000+',
    overLabel: 'Safe Jumps',
    ratedValue: '5 Star',
    ratedLabel: 'Experience',
    sinceValue: '7 Year',
    sinceLabel: 'Best Service',
    // FAQs and Packages
    faqs: [],
    packages: []
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const data = await getAdminLocations();
      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate image is uploaded
    if (!formData.image || formData.image.trim() === '') {
      alert('Please upload a location image before submitting');
      return;
    }
    
    // Validate packages - ensure all required fields are filled
    if (formData.packages && formData.packages.length > 0) {
      for (let i = 0; i < formData.packages.length; i++) {
        const pkg = formData.packages[i];
        if (!pkg.name || pkg.name.trim() === '') {
          alert(`Package #${i + 1}: Name is required`);
          return;
        }
        if (!pkg.price || pkg.price <= 0) {
          alert(`Package #${i + 1}: Price must be greater than 0`);
          return;
        }
      }
    }
    
    try {
      if (editingLocation) {
        await updateLocation(editingLocation._id, formData);
      } else {
        await createLocation(formData);
      }
      fetchLocations();
      resetForm();
    } catch (error) {
      console.error('Failed to save location:', error);
      alert('Failed to save location: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      location: location.location,
      price: location.price,
      description: location.description || '',
      image: location.image || '',
      displayOrder: location.displayOrder || 0,
      coordinates: location.coordinates || { lat: 0, lng: 0 },
      contactNumber: location.contactNumber || '',
      email: location.email || '',
      available: location.available,
      bookingEnabled: location.bookingEnabled !== undefined ? location.bookingEnabled : true,
      pricePerJumper: location.pricePerJumper || 0,
      maxJumpersPerSlot: location.maxJumpersPerSlot || 10,
      slotDuration: location.slotDuration || 60,
      partialPaymentEnabled: location.partialPaymentEnabled || false,
      partialPaymentAmount: location.partialPaymentAmount || 0,
      instagramLink: location.instagramLink || '',
      gmbLink: location.gmbLink || '',
      gmbRating: location.gmbRating || 4.9,
      gmbReviewCount: location.gmbReviewCount || 0,
      overValue: location.overValue || '44,000+',
      overLabel: location.overLabel || 'Safe Jumps',
      ratedValue: location.ratedValue || '5 Star',
      ratedLabel: location.ratedLabel || 'Experience',
      sinceValue: location.sinceValue || '7 Year',
      sinceLabel: location.sinceLabel || 'Best Service',
      faqs: location.faqs || [],
      packages: location.packages || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await deleteLocation(id);
        fetchLocations();
      } catch (error) {
        console.error('Failed to delete location:', error);
        alert('Failed to delete location');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      price: 0,
      description: '',
      image: '',
      displayOrder: 0,
      coordinates: { lat: 0, lng: 0 },
      contactNumber: '',
      email: '',
      available: true,
      bookingEnabled: true,
      pricePerJumper: 0,
      maxJumpersPerSlot: 10,
      slotDuration: 60,
      partialPaymentEnabled: false,
      partialPaymentAmount: 0,
      instagramLink: '',
      gmbLink: '',
      gmbRating: 4.9,
      gmbReviewCount: 0,
      overValue: '44,000+',
      overLabel: 'Safe Jumps',
      ratedValue: '5 Star',
      ratedLabel: 'Experience',
      sinceValue: '7 Year',
      sinceLabel: 'Best Service',
      faqs: [],
      packages: []
    });
    setEditingLocation(null);
    setShowModal(false);
    setActiveTab('basic');
  };

  // FAQ Management
  const addFAQ = () => {
    setFormData({
      ...formData,
      faqs: [...formData.faqs, { question: '', answer: '', order: formData.faqs.length }]
    });
  };

  const updateFAQ = (index, field, value) => {
    const newFaqs = [...formData.faqs];
    newFaqs[index][field] = value;
    setFormData({ ...formData, faqs: newFaqs });
  };

  const removeFAQ = (index) => {
    const newFaqs = formData.faqs.filter((_, i) => i !== index);
    setFormData({ ...formData, faqs: newFaqs });
  };

  // Package Management
  const addPackage = () => {
    setFormData({
      ...formData,
      packages: [...formData.packages, { name: '', description: '', price: 0, features: [], isPopular: false, order: formData.packages.length }]
    });
  };

  const updatePackage = (index, field, value) => {
    const newPackages = [...formData.packages];
    newPackages[index][field] = value;
    setFormData({ ...formData, packages: newPackages });
  };

  const removePackage = (index) => {
    const newPackages = formData.packages.filter((_, i) => i !== index);
    setFormData({ ...formData, packages: newPackages });
  };

  const addPackageFeature = (packageIndex) => {
    const newPackages = [...formData.packages];
    newPackages[packageIndex].features.push('');
    setFormData({ ...formData, packages: newPackages });
  };

  const updatePackageFeature = (packageIndex, featureIndex, value) => {
    const newPackages = [...formData.packages];
    newPackages[packageIndex].features[featureIndex] = value;
    setFormData({ ...formData, packages: newPackages });
  };

  const removePackageFeature = (packageIndex, featureIndex) => {
    const newPackages = [...formData.packages];
    newPackages[packageIndex].features = newPackages[packageIndex].features.filter((_, i) => i !== featureIndex);
    setFormData({ ...formData, packages: newPackages });
  };

  // Slot Management Functions
  const fetchSlotsForLocation = async (locationId, date = null) => {
    if (!locationId) return;
    try {
      const token = localStorage.getItem('adminToken');
      const params = { locationId };
      if (date) params.date = date;
      
      const response = await axios.get(`${API}/api/admin/slots`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setSlots([]);
    }
  };

  const createSlot = async (slotData) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/api/admin/slots`, slotData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingLocation) {
        fetchSlotsForLocation(editingLocation._id, selectedDate);
      }
    } catch (error) {
      console.error('Failed to create slot:', error);
      throw error;
    }
  };

  const deleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/api/admin/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingLocation) {
        fetchSlotsForLocation(editingLocation._id, selectedDate);
      }
    } catch (error) {
      console.error('Failed to delete slot:', error);
      alert('Failed to delete slot');
    }
  };

  const toggleSlotStatus = async (slotId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/api/admin/slots/${slotId}`, 
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (editingLocation) {
        fetchSlotsForLocation(editingLocation._id, selectedDate);
      }
    } catch (error) {
      console.error('Failed to toggle slot status:', error);
    }
  };

  const createBulkSlots = async (startDate, endDate, dayStart, dayEnd, duration, totalSeats = 10) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Generate slots array
      const slots = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Loop through each date in the range
      for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
        const dateStr = currentDate.toISOString().split('T')[0];
        
        // Parse time strings
        const [startHour, startMin] = dayStart.split(':').map(Number);
        const [endHour, endMin] = dayEnd.split(':').map(Number);
        
        // Calculate number of slots for the day
        const dayStartMinutes = startHour * 60 + startMin;
        const dayEndMinutes = endHour * 60 + endMin;
        
        // Create slots for this day
        for (let slotStart = dayStartMinutes; slotStart < dayEndMinutes; slotStart += duration) {
          const slotEnd = Math.min(slotStart + duration, dayEndMinutes);
          
          const startTime = `${String(Math.floor(slotStart / 60)).padStart(2, '0')}:${String(slotStart % 60).padStart(2, '0')}`;
          const endTime = `${String(Math.floor(slotEnd / 60)).padStart(2, '0')}:${String(slotEnd % 60).padStart(2, '0')}`;
          
          slots.push({
            locationId: editingLocation._id,
            locationName: editingLocation.name,
            date: dateStr,
            startTime,
            endTime,
            duration,
            totalSeats,
            bookedSeats: 0,
            availableSeats: totalSeats,
            isActive: true
          });
        }
      }
      
      if (slots.length === 0) {
        throw new Error('No slots generated. Please check your date range and times.');
      }
      
      await axios.post(`${API}/api/admin/slots/bulk`, {
        slots
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (editingLocation) {
        fetchSlotsForLocation(editingLocation._id, selectedDate);
      }
    } catch (error) {
      console.error('Failed to create bulk slots:', error);
      throw error;
    }
  };

  // Fetch slots when editing location and date changes
  useEffect(() => {
    if (editingLocation && activeTab === 'slots') {
      fetchSlotsForLocation(editingLocation._id, selectedDate);
    }
  }, [editingLocation, activeTab, selectedDate]);


  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-white text-center py-12">Loading locations...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Locations Manager</h1>
          <p className="text-gray-400">Manage bungy jumping locations</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-lg transition-colors">
          <Plus size={20} />
          <span>New Location</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input type="text" placeholder="Search locations..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLocations.map((location) => (
          <div key={location._id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-400 transition-colors">
            {location.imageUrl && <img src={location.imageUrl} alt={location.name} className="w-full h-48 object-cover" />}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${location.available ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {location.available ? 'Available' : 'Unavailable'}
                  </span>
                  <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                    Order: {location.displayOrder || 0}
                  </span>
                </div>
                <span className="text-yellow-400 font-bold">₹{location.price}</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{location.name}</h3>
              <p className="text-gray-400 text-sm mb-4 flex items-center"><MapPin size={14} className="mr-1" />{location.location}</p>
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(location)} className="flex-1 p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-colors"><Edit2 size={16} className="mx-auto" /></button>
                <button onClick={() => handleDelete(location._id)} className="flex-1 p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"><Trash2 size={16} className="mx-auto" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredLocations.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No locations found</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">{editingLocation ? 'Edit Location' : 'Add New Location'}</h2>
              <button
                type="button"
                onClick={resetForm}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-700 px-6">
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'basic' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-white'}`}
              >
                Basic Info
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('social')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'social' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-white'}`}
              >
                Social & GMB
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('faqs')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'faqs' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-white'}`}
              >
                FAQs
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('packages')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'packages' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-white'}`}
              >
                Packages
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('slots')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'slots' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-white'}`}
              >
                Slots
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
              <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Location *</label>
                  <select 
                    value={formData.location} 
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
                    required 
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="">Select Location</option>
                    <option value="Goa">Goa</option>
                    <option value="Rishikesh">Rishikesh</option>
                    <option value="Bangalore">Bangalore</option>
                    <option value="Delhi NCR">Delhi NCR</option>
                    <option value="Mumbai">Mumbai</option>
                    <option value="Pune">Pune</option>
                    <option value="Hyderabad">Hyderabad</option>
                    <option value="Kolkata">Kolkata</option>
                    <option value="Chennai">Chennai</option>
                    <option value="Jaipur">Jaipur</option>
                    <option value="Udaipur">Udaipur</option>
                    <option value="Manali">Manali</option>
                    <option value="Lonavala">Lonavala</option>
                    <option value="Mysore">Mysore</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-white font-medium mb-2 block">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Price (₹) *</label>
                  <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })} required min="0" className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Display Order</label>
                  <input 
                    type="number" 
                    value={formData.displayOrder} 
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} 
                    min="0" 
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    placeholder="0 = first, higher = later" 
                  />
                  <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
                </div>
              </div>
              <FileUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                type="images"
                accept="image/*"
                label="Location Image *"
              />
              {formData.image && (
                <div className="text-green-400 text-sm mt-2">
                  ✓ Image uploaded successfully
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Latitude</label>
                  <input type="number" step="any" value={formData.coordinates.lat} onChange={(e) => setFormData({ ...formData, coordinates: { ...formData.coordinates, lat: parseFloat(e.target.value) } })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Longitude</label>
                  <input type="number" step="any" value={formData.coordinates.lng} onChange={(e) => setFormData({ ...formData, coordinates: { ...formData.coordinates, lng: parseFloat(e.target.value) } })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Contact Number</label>
                  <input type="tel" value={formData.contactNumber} onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="available" checked={formData.available} onChange={(e) => setFormData({ ...formData, available: e.target.checked })} className="w-5 h-5 bg-gray-700 border-gray-600 rounded" />
                <label htmlFor="available" className="text-white font-medium">Available for booking</label>
              </div>
              
              {/* Booking Settings Section */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <h3 className="text-lg font-bold text-yellow-400 mb-4">Booking Settings</h3>
                
                <div className="flex items-center space-x-3 mb-4">
                  <input 
                    type="checkbox" 
                    id="bookingEnabled" 
                    checked={formData.bookingEnabled} 
                    onChange={(e) => setFormData({ ...formData, bookingEnabled: e.target.checked })} 
                    className="w-5 h-5 bg-gray-700 border-gray-600 rounded" 
                  />
                  <label htmlFor="bookingEnabled" className="text-white font-medium">Enable Online Booking</label>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white font-medium mb-2 block">Price Per Jumper (₹) *</label>
                    <input 
                      type="number" 
                      value={formData.pricePerJumper} 
                      onChange={(e) => setFormData({ ...formData, pricePerJumper: parseFloat(e.target.value) || 0 })} 
                      min="0" 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., 3500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Price charged per person for booking</p>
                  </div>
                  
                  <div>
                    <label className="text-white font-medium mb-2 block">Max Jumpers Per Slot</label>
                    <input 
                      type="number" 
                      value={formData.maxJumpersPerSlot} 
                      onChange={(e) => setFormData({ ...formData, maxJumpersPerSlot: parseInt(e.target.value) || 10 })} 
                      min="1" 
                      max="50"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                    />
                  </div>
                  
                  <div>
                    <label className="text-white font-medium mb-2 block">Slot Duration (minutes)</label>
                    <input 
                      type="number" 
                      value={formData.slotDuration} 
                      onChange={(e) => setFormData({ ...formData, slotDuration: parseInt(e.target.value) || 60 })} 
                      min="15" 
                      step="15"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                    />
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <input 
                      type="checkbox" 
                      id="partialPaymentEnabled" 
                      checked={formData.partialPaymentEnabled} 
                      onChange={(e) => setFormData({ ...formData, partialPaymentEnabled: e.target.checked })} 
                      className="w-5 h-5 bg-gray-600 border-gray-500 rounded" 
                    />
                    <label htmlFor="partialPaymentEnabled" className="text-white font-medium">Enable Partial Payment</label>
                  </div>
                  
                  {formData.partialPaymentEnabled && (
                    <div>
                      <label className="text-white font-medium mb-2 block">Partial Payment Amount (₹)</label>
                      <input 
                        type="number" 
                        value={formData.partialPaymentAmount} 
                        onChange={(e) => setFormData({ ...formData, partialPaymentAmount: parseFloat(e.target.value) || 0 })} 
                        min="0" 
                        className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                        placeholder="e.g., 1000"
                      />
                      <p className="text-xs text-gray-400 mt-1">Amount to be paid upfront (rest at venue)</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Stats Section */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <h3 className="text-lg font-bold text-yellow-400 mb-4">Location Stats (Over, Rated, Since)</h3>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Over Section */}
                  <div className="space-y-2">
                    <label className="text-white font-medium mb-2 block">Over - Value</label>
                    <input 
                      type="text" 
                      value={formData.overValue} 
                      onChange={(e) => setFormData({ ...formData, overValue: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., 44,000+"
                    />
                    <label className="text-white font-medium mb-2 block">Over - Label</label>
                    <input 
                      type="text" 
                      value={formData.overLabel} 
                      onChange={(e) => setFormData({ ...formData, overLabel: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., Safe Jumps"
                    />
                  </div>
                  
                  {/* Rated Section */}
                  <div className="space-y-2">
                    <label className="text-white font-medium mb-2 block">Rated - Value</label>
                    <input 
                      type="text" 
                      value={formData.ratedValue} 
                      onChange={(e) => setFormData({ ...formData, ratedValue: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., 5 Star"
                    />
                    <label className="text-white font-medium mb-2 block">Rated - Label</label>
                    <input 
                      type="text" 
                      value={formData.ratedLabel} 
                      onChange={(e) => setFormData({ ...formData, ratedLabel: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., Experience"
                    />
                  </div>
                  
                  {/* Since Section */}
                  <div className="space-y-2">
                    <label className="text-white font-medium mb-2 block">Since - Value</label>
                    <input 
                      type="text" 
                      value={formData.sinceValue} 
                      onChange={(e) => setFormData({ ...formData, sinceValue: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., 7 Year"
                    />
                    <label className="text-white font-medium mb-2 block">Since - Label</label>
                    <input 
                      type="text" 
                      value={formData.sinceLabel} 
                      onChange={(e) => setFormData({ ...formData, sinceLabel: e.target.value })} 
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      placeholder="e.g., Best Service"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">These stats appear on the location detail page showing achievements and experience.</p>
              </div>
              </>
              )}

              {/* Social Media & GMB Tab */}
              {activeTab === 'social' && (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-yellow-400 mb-4">Social Media & Google My Business</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white font-medium mb-2 block flex items-center">
                        <Instagram size={16} className="mr-2" />
                        Instagram Link
                      </label>
                      <input 
                        type="url" 
                        value={formData.instagramLink} 
                        onChange={(e) => setFormData({ ...formData, instagramLink: e.target.value })} 
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                        placeholder="https://instagram.com/yourlocation"
                      />
                    </div>
                    
                    <div>
                      <label className="text-white font-medium mb-2 block flex items-center">
                        <ExternalLink size={16} className="mr-2" />
                        Google My Business Link
                      </label>
                      <input 
                        type="url" 
                        value={formData.gmbLink} 
                        onChange={(e) => setFormData({ ...formData, gmbLink: e.target.value })} 
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                        placeholder="https://goo.gl/maps/..."
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white font-medium mb-2 block flex items-center">
                        <Star size={16} className="mr-2" />
                        GMB Rating
                      </label>
                      <input 
                        type="number" 
                        step="0.1"
                        min="0"
                        max="5"
                        value={formData.gmbRating} 
                        onChange={(e) => setFormData({ ...formData, gmbRating: parseFloat(e.target.value) || 0 })} 
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      />
                    </div>
                    
                    <div>
                      <label className="text-white font-medium mb-2 block">GMB Review Count</label>
                      <input 
                        type="number" 
                        min="0"
                        value={formData.gmbReviewCount} 
                        onChange={(e) => setFormData({ ...formData, gmbReviewCount: parseInt(e.target.value) || 0 })} 
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                      />
                    </div>
                  </div>
                </div>
              </>
              )}

              {/* FAQs Tab */}
              {activeTab === 'faqs' && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-yellow-400">Frequently Asked Questions</h3>
                    <button
                      type="button"
                      onClick={addFAQ}
                      className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-medium px-4 py-2 rounded-lg transition-colors"
                    >
                      <Plus size={16} />
                      <span>Add FAQ</span>
                    </button>
                  </div>
                  
                  {formData.faqs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p>No FAQs added yet. Click &quot;Add FAQ&quot; to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.faqs.map((faq, index) => (
                        <div key={index} className="bg-gray-700 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-yellow-400 font-medium">FAQ #{index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeFAQ(index)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="text-white font-medium mb-2 block">Question</label>
                              <input
                                type="text"
                                value={faq.question}
                                onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                                className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                                placeholder="Enter the question..."
                              />
                            </div>
                            
                            <div>
                              <label className="text-white font-medium mb-2 block">Answer</label>
                              <textarea
                                value={faq.answer}
                                onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                                placeholder="Enter the answer..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
              )}

              {/* Packages Tab */}
              {activeTab === 'packages' && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-yellow-400">Packages</h3>
                    <button
                      type="button"
                      onClick={addPackage}
                      className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-medium px-4 py-2 rounded-lg transition-colors"
                    >
                      <Package size={16} />
                      <span>Add Package</span>
                    </button>
                  </div>
                  
                  {formData.packages.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p>No packages added yet. Click &quot;Add Package&quot; to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.packages.map((pkg, index) => (
                        <div key={index} className="bg-gray-700 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-yellow-400 font-medium">Package #{index + 1}</span>
                            <div className="flex items-center space-x-2">
                              <label className="flex items-center space-x-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={pkg.isPopular}
                                  onChange={(e) => updatePackage(index, 'isPopular', e.target.checked)}
                                  className="w-4 h-4 bg-gray-600 border-gray-500 rounded"
                                />
                                <span className="text-yellow-400">Popular</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => removePackage(index)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="text-white font-medium mb-2 block">
                                Package Name <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                value={pkg.name}
                                onChange={(e) => updatePackage(index, 'name', e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                                placeholder="e.g., Basic Jump"
                              />
                            </div>
                            
                            <div>
                              <label className="text-white font-medium mb-2 block">
                                Price (₹) <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="number"
                                value={pkg.price}
                                onChange={(e) => updatePackage(index, 'price', parseFloat(e.target.value) || 0)}
                                min="0"
                                required
                                className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-white font-medium">Features</label>
                              <button
                                type="button"
                                onClick={() => addPackageFeature(index)}
                                className="text-yellow-400 hover:text-yellow-300 text-sm transition-colors"
                              >
                                + Add Feature
                              </button>
                            </div>
                            
                            <div className="space-y-2">
                              {pkg.features.map((feature, featureIndex) => (
                                <div key={featureIndex} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={feature}
                                    onChange={(e) => updatePackageFeature(index, featureIndex, e.target.value)}
                                    className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-yellow-400"
                                    placeholder="Feature description..."
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePackageFeature(index, featureIndex)}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
              )}

              {/* Slots Tab */}
              {activeTab === 'slots' && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-yellow-400">Slot Management</h3>
                    {!editingLocation && (
                      <p className="text-sm text-gray-400">Save location first to manage slots</p>
                    )}
                  </div>

                  {editingLocation ? (
                    <>
                      {/* Date Filter */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <label className="text-white font-medium mb-2 block flex items-center">
                          <Calendar size={16} className="mr-2" />
                          Filter by Date
                        </label>
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                        />
                        {selectedDate && (
                          <div className="mt-2 text-sm text-gray-300">
                            {slots.length === 0 ? (
                              <span className="text-red-400">⚠️ No slots configured - Date is OFF</span>
                            ) : (
                              <span className="text-green-400">✓ {slots.length} slot(s) configured</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Quick Add Slot Form */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-white font-medium mb-3 flex items-center">
                          <Clock size={16} className="mr-2" />
                          Quick Add Slot
                        </h4>
                        <div className="grid md:grid-cols-4 gap-3">
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Date</label>
                            <input
                              type="date"
                              id="quickSlotDate"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Start Time</label>
                            <input
                              type="time"
                              id="quickSlotStart"
                              defaultValue="09:00"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">End Time</label>
                            <input
                              type="time"
                              id="quickSlotEnd"
                              defaultValue="10:00"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Total Seats</label>
                            <input
                              type="number"
                              id="quickSlotSeats"
                              defaultValue="10"
                              min="1"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            const date = document.getElementById('quickSlotDate').value;
                            const startTime = document.getElementById('quickSlotStart').value;
                            const endTime = document.getElementById('quickSlotEnd').value;
                            const totalSeats = parseInt(document.getElementById('quickSlotSeats').value);

                            if (!date || !startTime || !endTime) {
                              alert('Please fill all fields');
                              return;
                            }

                            try {
                              await createSlot({
                                locationId: editingLocation._id,
                                locationName: editingLocation.name,
                                date,
                                startTime,
                                endTime,
                                duration: 60,
                                totalSeats,
                                bookedSeats: 0,
                                availableSeats: totalSeats,
                                isActive: true
                              });
                              alert('Slot created successfully!');
                              document.getElementById('quickSlotDate').value = '';
                            } catch (error) {
                              alert('Failed to create slot: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          className="mt-3 w-full bg-yellow-400 hover:bg-yellow-300 text-black font-medium px-4 py-2 rounded-lg transition-colors flex items-center justify-center"
                        >
                          <Plus size={16} className="mr-2" />
                          Add Slot
                        </button>
                      </div>

                      {/* Bulk Create Slots */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-white font-medium mb-3">Bulk Create Slots</h4>
                        <div className="grid md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Start Date</label>
                            <input
                              type="date"
                              id="bulkStartDate"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">End Date</label>
                            <input
                              type="date"
                              id="bulkEndDate"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-3 mb-3">
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Day Start</label>
                            <input
                              type="time"
                              id="bulkDayStart"
                              defaultValue="09:00"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Day End</label>
                            <input
                              type="time"
                              id="bulkDayEnd"
                              defaultValue="18:00"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-300 mb-1 block">Slot Duration (min)</label>
                            <input
                              type="number"
                              id="bulkDuration"
                              defaultValue="60"
                              min="15"
                              step="15"
                              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:border-yellow-400"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            const startDate = document.getElementById('bulkStartDate').value;
                            const endDate = document.getElementById('bulkEndDate').value;
                            const dayStart = document.getElementById('bulkDayStart').value;
                            const dayEnd = document.getElementById('bulkDayEnd').value;
                            const duration = parseInt(document.getElementById('bulkDuration').value);

                            if (!startDate || !endDate || !dayStart || !dayEnd) {
                              alert('Please fill all fields');
                              return;
                            }

                            try {
                              await createBulkSlots(startDate, endDate, dayStart, dayEnd, duration, 10);
                              alert('Bulk slots created successfully!');
                              // Clear form
                              document.getElementById('bulkStartDate').value = '';
                              document.getElementById('bulkEndDate').value = '';
                            } catch (error) {
                              alert('Failed to create bulk slots: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                        >
                          Create Bulk Slots
                        </button>
                      </div>

                      {/* Slots List */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-white font-medium mb-3">
                          {selectedDate ? `Slots for ${new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}` : 'All Slots'}
                        </h4>
                        
                        {slots.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            {selectedDate ? (
                              <div>
                                <p className="text-red-400 font-medium mb-2">⚠️ No slots for this date</p>
                                <p className="text-sm">This date is considered OFF. Add slots to make it available.</p>
                              </div>
                            ) : (
                              <p>Select a date to view slots or add a new slot above</p>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {slots.map((slot) => (
                              <div key={slot._id} className="bg-gray-600 p-3 rounded flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <Clock size={16} className="text-yellow-400" />
                                  <div>
                                    <p className="text-white font-medium">
                                      {slot.startTime} - {slot.endTime}
                                    </p>
                                    <p className="text-sm text-gray-300 flex items-center space-x-2">
                                      <Users size={14} />
                                      <span>{slot.availableSeats}/{slot.totalSeats} seats available</span>
                                      {slot.isActive ? (
                                        <span className="text-green-400">● Active</span>
                                      ) : (
                                        <span className="text-red-400">● Inactive</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => toggleSlotStatus(slot._id, slot.isActive)}
                                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                      slot.isActive 
                                        ? 'bg-red-500 hover:bg-red-600 text-white' 
                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                    }`}
                                  >
                                    {slot.isActive ? 'Deactivate' : 'Activate'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteSlot(slot._id)}
                                    className="text-red-400 hover:text-red-300 transition-colors p-2"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Please save the location first before managing slots</p>
                    </div>
                  )}
                </div>
              </>
              )}

              )}
              
              <div className="flex justify-end space-x-4 pt-4">
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors">{editingLocation ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsManager;